import * as mobx from 'mobx';
import { addDays, addMinutes, isAfter, isBefore, isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { IZoomMeetingDTO } from '../../../../../api/zoom.dao';

interface IMeetingDetails extends IZoomMeetingDTO {
    isEditMode?: boolean;
}
const defaultMeetingData: IMeetingDetails = {
    startTime: null,
    endTime: null,
    timeZone: '',
    topic: '',
    agenda: '',
    zoomGroupId: null,
    isEditMode: false
};

class MeetingFieldsViewModel {
    @mobx.observable
    public timeZone: string;
    @mobx.observable
    public topic: string;
    @mobx.observable
    public description: string;
    @mobx.observable
    public startDateTime: Date;
    @mobx.observable
    public endDateTime: Date;
    @mobx.observable
    public zoomMeetingId: string;
    @mobx.observable
    public selectedGroupId: number;
    @mobx.observable
    private isChecked: boolean;
    @mobx.observable
    public isEditMode: boolean;

    constructor(
        public apiMeeting: IMeetingDetails = defaultMeetingData
    ) {
        apiMeeting = this.fromApiToDTO(apiMeeting);
        this.startDateTime = apiMeeting.startTime ? utcToZonedTime(new Date(apiMeeting.startTime), apiMeeting.timeZone) : apiMeeting.startTime;
        this.endDateTime = apiMeeting.endTime ? utcToZonedTime(new Date(apiMeeting.endTime), apiMeeting.timeZone) : apiMeeting.endTime;
        this.timeZone = apiMeeting.timeZone;
        this.topic = apiMeeting.topic;
        this.description = apiMeeting.agenda;
        this.zoomMeetingId = apiMeeting.zoomId;
        this.isEditMode = !!apiMeeting.isEditMode;
    }

    public getStartDateTime = () => {
        return this.startDateTime;
    };

    public getEndDateTime = () => {
        return this.endDateTime;
    };

    public getTimeZone = () => {
        return this.timeZone;
    };

    public getTopic = () => {
        return this.topic;
    };

    public getGroup = () => {
        return this.selectedGroupId;
    };

    public getMaxEndDateTime = () => {
        if (!this.startDateTime) {
            return null;
        } else {
            return addDays(this.startDateTime, 1);
        }
    };

    public getMinEndTime = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!this.startDateTime) {
            return today;
        }
        return addMinutes(this.startDateTime, 15);
    };

    public getMinStartDateTime = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!this.endDateTime) {
            return today;
        } else {
            const minStartDate = addDays(this.endDateTime, -1);
            return minStartDate < today ? today : minStartDate;
        }
    };

    public getMaxStartDateTime = () => {
        return this.endDateTime || null;
    };

    public showErrorMessages = () => {
        return this.isChecked;
    };

    @mobx.action
    public preSubmitValidation = () => {
        this.isChecked = true;

        return !this.getEndTimeError() &&
            !this.getStartTimeError() &&
            !this.getTimeZoneError() &&
            !this.getTopicError() &&
            !this.getTopicLengthError() &&
            !this.getDescriptionLengthError();
    };

    @mobx.action
    public setStartDateTime = (value: Date) => {
        let newValue = value;
        if (isBefore(newValue, this.getMinStartDateTime())) {
            newValue = this.getMinStartDateTime();
        } else if (isAfter(newValue, this.getMaxStartDateTime())) {
            newValue = this.getMaxStartDateTime();
        }
        if (newValue) {
            newValue.setSeconds(0);
        }

        this.startDateTime = newValue;
    };

    @mobx.action
    public setEndDateTime = (value: Date) => {
        let newValue = value;
        if (isBefore(newValue, this.getMinEndTime())) {
            if (isSameDay(newValue, this.getMinEndTime())) {
                newValue = this.getMinEndTime();
            } else {
                newValue.setDate(this.getMinEndTime().getDate());
                newValue.setMonth(this.getMinEndTime().getMonth());
                newValue.setFullYear(this.getMinEndTime().getFullYear());
            }
        } else if (isAfter(newValue, this.getMaxEndDateTime())) {
            newValue = this.getMaxEndDateTime();
        }
        if (newValue) {
            newValue.setSeconds(0);
        }

        this.endDateTime = newValue;
    };

    @mobx.action
    public setGroup = (value: number) => {
        this.selectedGroupId = value;
    };

    @mobx.action
    public setTimeZone = (value: string) => {
        this.timeZone = value;
    };

    @mobx.action
    public hideErrorMessages = () => {
        this.isChecked = false;
    };

    public getTopicLengthError = () => {
        return this.topic.length > 200 ? 'Maximun topic length is 200 symbols' : null;
    };

    public getDescriptionLengthError = () => {
        return this.description.length > 2000 ? 'Maximun description length is 2000 symbols' : null;
    };

    public isNeedShowStartBeforeError = () => {
        return isBefore(this.startDateTime, addMinutes(Date.now(), 4))
            ? 'Start Date can\'t be before current date'
            : null;
    };

    public isNeedShowEndBeforeError = () => {
        return isBefore(this.endDateTime, addMinutes(Date.now(), 4))
            ? 'End Date can\'t be before current date'
            : null;
    };

    public getStartTimeError = () => {
        return !this.startDateTime ? 'Start time is required' : null;
    };

    public getTimeBeforeError = () => {
        return 'The date can\'t be before now';
    };

    public getEndTimeError = () => {
        return !this.endDateTime ? 'End time is required' : null;
    };

    public getTopicError = () => {
        return !this.topic.trim() ? 'Topic is required' : null;
    };

    public getTimeZoneError = () => {
        return !this.timeZone ? 'Timezone is required' : null;
    };

    public getGroupError = () => {
        return !this.selectedGroupId ? 'Group is required' : null;
    };

    private fromApiToDTO = (meeting: IZoomMeetingDTO) => {
        return {
            startTime: meeting.startTime,
            endTime: meeting.endTime,
            timeZone: meeting.timeZone,
            topic: meeting.topic,
            agenda: meeting.agenda,
            group: meeting.zoomGroupId,
        };
    };
}

export { MeetingFieldsViewModel };
